import type { Analytics } from 'firebase/analytics';

export const ANALYTICS_CONSENT_KEY = 'alxnsv.analytics-consent.v1';
export type AnalyticsConsent = 'accepted' | 'declined';
type Environment = Record<string, unknown>;
type AnalyticsSdk = typeof import('firebase/analytics');
type StorageReader = Pick<Storage, 'getItem'>;

const publicPaths = new Set([
  '/',
  '/eldgard',
  '/eldgard/docs',
  '/eldgard/privacy',
  '/eldgard/terms',
  '/eldgard/support',
  '/eldgard/data-deletion',
  '/spoolmark',
  '/spoolmark/docs',
  '/spoolmark/privacy',
  '/spoolmark/terms',
  '/spoolmark/support',
  '/spoolmark/data-deletion',
  '/spoolmark/blog',
  '/spoolmark/journal',
  '/spoolmark/blog/bambu-lab-spool-scanning-on-ios',
]);
const campaignKeys = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
];
const adsDenied = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
} as const;

export function getAnalyticsConfig(environment: Environment) {
  const read = (key: string) => {
    const value = environment[key];
    return typeof value === 'string' ? value.trim() : '';
  };
  const config = {
    apiKey: read('VITE_FIREBASE_API_KEY'),
    projectId: read('VITE_FIREBASE_PROJECT_ID'),
    appId: read('VITE_FIREBASE_APP_ID'),
    measurementId: read('VITE_FIREBASE_MEASUREMENT_ID'),
  };
  if (
    !config.apiKey ||
    !config.projectId ||
    !/^1:\d+:web:[a-z\d]+$/i.test(config.appId) ||
    !/^G-[A-Z\d]+$/.test(config.measurementId)
  ) {
    return null;
  }
  return config;
}

export function isAnalyticsHostAllowed(
  hostname: string,
  environment: Environment,
) {
  if (hostname === 'alxnsv.io' || hostname === 'www.alxnsv.io') return true;
  return (
    ['localhost', '127.0.0.1', '[::1]', '::1'].includes(hostname) &&
    environment.DEV === true &&
    environment.VITE_ANALYTICS_DEBUG === 'true'
  );
}

export function parseAnalyticsConsent(
  value: string | null,
): AnalyticsConsent | null {
  return value === 'accepted' || value === 'declined' ? value : null;
}

export function readStoredAnalyticsConsent(
  storage: StorageReader,
): AnalyticsConsent | null {
  try {
    return parseAnalyticsConsent(storage.getItem(ANALYTICS_CONSENT_KEY));
  } catch {
    return null;
  }
}

export function analyticsPagePath(pathname: string) {
  const path = pathname.replace(/\/+$/, '') || '/';
  return publicPaths.has(path) ? path : '/404';
}

// Only public routes and short campaign labels may leave the browser. Do not put
// names, email addresses, user IDs, or other personal data in campaign labels.
export function sanitizedPageUrl(href: string) {
  const original = new URL(href);
  const sanitized = new URL(
    analyticsPagePath(original.pathname),
    original.origin,
  );
  for (const key of campaignKeys) {
    const value = original.searchParams.get(key);
    if (value && /^[a-z\d][a-z\d_-]{0,63}$/i.test(value)) {
      sanitized.searchParams.set(key, value);
    }
  }
  return sanitized.href;
}

export function sanitizedReferrer(referrer: string) {
  try {
    const url = new URL(referrer);
    return ['https:', 'http:'].includes(url.protocol) ? `${url.origin}/` : '';
  } catch {
    return '';
  }
}

export function analyticsClickEvent(
  href: string,
  pagePath: string,
  siteOrigin = 'https://alxnsv.io',
) {
  let destination: URL;
  try {
    destination = new URL(href, `${siteOrigin}${analyticsPagePath(pagePath)}`);
  } catch {
    return null;
  }
  const product = pagePath.startsWith('/eldgard')
    ? 'eldgard'
    : pagePath.startsWith('/spoolmark')
      ? 'spoolmark'
      : 'studio';
  if (
    pagePath === '/eldgard' &&
    destination.origin === siteOrigin &&
    analyticsPagePath(destination.pathname) === '/eldgard' &&
    destination.hash === '#early-access'
  ) {
    // This opens the form section; it does not confirm a submitted lead.
    return { name: 'early_access_click', params: { product: 'eldgard' } };
  }
  if (destination.protocol === 'mailto:') {
    // Inspect locally to recognize the existing CTA; never send its address or
    // subject. Opening a mail client is not a confirmed lead or sign-up.
    const earlyAccess =
      destination.searchParams.get('subject') === 'Eldgard early access';
    return {
      name: earlyAccess ? 'early_access_click' : 'contact_click',
      params: { product: earlyAccess ? 'eldgard' : product },
    };
  }
  if (destination.protocol !== 'https:') return null;
  if (
    destination.hostname === 'play.google.com' &&
    destination.pathname === '/store/apps/details' &&
    destination.searchParams.get('id') === 'io.alxnsv.bambu_spool_scanner'
  ) {
    return {
      name: 'app_store_click',
      params: { product: 'spoolmark', store: 'google_play' },
    };
  }
  return null;
}

export function ownedAnalyticsCookieNames(cookieHeader: string) {
  return cookieHeader
    .split(';')
    .map((cookie) => cookie.trim().split('=')[0])
    .filter((name) => /^alxnsv_ga(?:_[a-z\d]+)?$/i.test(name));
}

let sessionConsent: AnalyticsConsent | null | undefined;
let sdk: AnalyticsSdk | undefined;
let instance: Analytics | undefined;
let starting: { promise: Promise<void>; generation: number } | undefined;
let generation = 0;
let pageViewSent = false;
let listening = false;

const environment: Environment = import.meta.env ?? {};
const config = getAnalyticsConfig(environment);

export function analyticsIsAvailable() {
  return (
    typeof window !== 'undefined' &&
    config !== null &&
    isAnalyticsHostAllowed(window.location.hostname, environment)
  );
}

export function getAnalyticsConsent() {
  if (sessionConsent !== undefined) return sessionConsent;
  try {
    return readStoredAnalyticsConsent(window.localStorage);
  } catch {
    return null;
  }
}

function disableCollection(disabled: boolean) {
  if (!config) return;
  (window as unknown as Record<string, unknown>)[
    `ga-disable-${config.measurementId}`
  ] = disabled;
}

function deleteAnalyticsCookies() {
  const domains = new Set([
    '',
    window.location.hostname,
    `.${window.location.hostname}`,
  ]);
  if (window.location.hostname.endsWith('.alxnsv.io')) {
    domains.add('alxnsv.io');
    domains.add('.alxnsv.io');
  }
  for (const name of ownedAnalyticsCookieNames(document.cookie)) {
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain ? `; Domain=${domain}` : ''}`;
    }
  }
}

function pageParameters() {
  return {
    page_location: sanitizedPageUrl(window.location.href),
    page_path: analyticsPagePath(window.location.pathname),
    page_referrer: sanitizedReferrer(document.referrer),
    page_title: document.title,
    ...(environment.DEV === true && environment.VITE_ANALYTICS_DEBUG === 'true'
      ? { debug_mode: true }
      : {}),
  };
}

function onDocumentClick(event: MouseEvent) {
  if (!instance || !sdk || getAnalyticsConsent() !== 'accepted') return;
  const anchor =
    event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!(anchor instanceof HTMLAnchorElement)) return;
  const click = analyticsClickEvent(
    anchor.href,
    analyticsPagePath(window.location.pathname),
    window.location.origin,
  );
  if (click)
    sdk.logEvent(instance, click.name, {
      ...pageParameters(),
      ...click.params,
    });
}

export async function startAnalytics() {
  if (
    !analyticsIsAvailable() ||
    getAnalyticsConsent() !== 'accepted' ||
    !config
  )
    return;
  if (starting) {
    const pending = starting;
    await pending.promise;
    // A visitor can decline and re-allow while the first import is in flight.
    // Retry only an invalidated start, never an unsupported or failed SDK.
    if (
      !instance &&
      generation !== pending.generation &&
      getAnalyticsConsent() === 'accepted'
    )
      return startAnalytics();
    return;
  }
  const expectedGeneration = generation;
  const promise = (async () => {
    try {
      // Both Firebase chunks and Google's tag remain unloaded before opt-in.
      const [appSdk, analyticsSdk] = await Promise.all([
        import('firebase/app'),
        import('firebase/analytics'),
      ]);
      if (!(await analyticsSdk.isSupported())) return;
      if (
        generation !== expectedGeneration ||
        getAnalyticsConsent() !== 'accepted'
      )
        return;
      sdk = analyticsSdk;
      disableCollection(false);
      sdk.setConsent({ ...adsDenied, analytics_storage: 'granted' });
      const app =
        appSdk
          .getApps()
          .find((candidate) => candidate.name === 'website-analytics') ??
        appSdk.initializeApp(config, 'website-analytics');
      instance ??= sdk.initializeAnalytics(app, {
        config: {
          ...pageParameters(),
          send_page_view: false,
          allow_google_signals: false,
          allow_ad_personalization_signals: false,
          cookie_prefix: 'alxnsv',
          cookie_domain: window.location.hostname,
          cookie_path: '/',
          cookie_expires: 60 * 60 * 24 * 180,
          cookie_flags: 'SameSite=Lax;Secure',
        },
      });
      sdk.setAnalyticsCollectionEnabled(instance, true);
      if (!pageViewSent) {
        // A module-level guard keeps React StrictMode from double-counting.
        pageViewSent = true;
        sdk.logEvent(instance, 'page_view', pageParameters());
      }
      if (!listening) {
        document.addEventListener('click', onDocumentClick);
        listening = true;
      }
    } catch {
      // Blocking analytics must never prevent the site from working.
    }
  })();
  const pending = { promise, generation: expectedGeneration };
  starting = pending;
  await pending.promise;
  if (starting === pending) starting = undefined;
}

function stopAnalytics(reload: boolean) {
  generation += 1;
  disableCollection(true);
  if (listening) document.removeEventListener('click', onDocumentClick);
  listening = false;
  if (sdk && instance) {
    sdk.setAnalyticsCollectionEnabled(instance, false);
    sdk.setConsent({ ...adsDenied, analytics_storage: 'denied' });
  }
  deleteAnalyticsCookies();
  // Unload the already running tag and its internal listeners after revocation.
  // If storage is blocked, keep this document disabled instead of re-reading an
  // old persisted acceptance on reload.
  if (instance && reload) window.location.reload();
}

export function chooseAnalyticsConsent(choice: AnalyticsConsent) {
  sessionConsent = choice;
  let persisted = false;
  try {
    window.localStorage.setItem(ANALYTICS_CONSENT_KEY, choice);
    persisted = true;
  } catch {
    // Honor the choice for this document when browser storage is unavailable.
  }
  window.dispatchEvent(new Event('alxnsv:analytics-consent'));
  if (choice === 'accepted') void startAnalytics();
  else stopAnalytics(persisted);
}

export function syncAnalyticsConsent() {
  sessionConsent = undefined;
  const consent = getAnalyticsConsent();
  if (consent === 'accepted') void startAnalytics();
  else stopAnalytics(true);
  return consent;
}

export function getAnalyticsSnapshot() {
  return analyticsIsAvailable() ? getAnalyticsConsent() : 'unavailable';
}

export function subscribeAnalyticsConsent(onChange: () => void) {
  const onStorage = (event: StorageEvent) => {
    if (event.key !== ANALYTICS_CONSENT_KEY && event.key !== null) return;
    syncAnalyticsConsent();
    onChange();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener('alxnsv:analytics-consent', onChange);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener('alxnsv:analytics-consent', onChange);
  };
}
